import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import NavBar from '../common/NavBar';
import Footer from '../Footer';
import { useIsMobile } from '../../Utils/useIsMobile';
import MobileNavBar from '../common/MobileNavBar';
import MobileFooter from '../MobileFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import { MISSING_DOCS_MODAL_SEEN_KEY } from '../../Utils/missingSettlementDocsModal';
import {
  FOOTER_BREAKPOINT,
  MOBILE_BREAKPOINT,
  convertToVwMob,
} from '../../Utils/responsive';
import { PATHS } from '../../routes/paths';
import postIcon from '../../assets/Icon/sidbar/post_icon.png';
import postActiveIcon from '../../assets/Icon/sidbar/post_active_icon.png';
import sponsorIcon from '../../assets/Icon/sidbar/sponsor_icon.png';
import sponsorActiveIcon from '../../assets/Icon/sidbar/sponsor_active_icon.png';
import payIcon from '../../assets/Icon/sidbar/pay_icon.png';
import payActiveIcon from '../../assets/Icon/sidbar/pay_active_icon.png';
import projectIcon from '../../assets/Icon/sidbar/project_icon.png';
import projectActiveIcon from '../../assets/Icon/sidbar/project_active_icon.png';
import MissingDocumentsModal from '../common/MissingDocumentsModal';
import { isIndividualUserType } from '../../Utils/userType';

const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
const SIGNUP_SKIP_ACCOUNT_FIRST_KEY = 'missionfund.signupSkippedAccountFirst';


type ProjectSidebarActive = 'posts' | 'donors' | 'payments' | 'settings';
const PROJECT_SIDEBAR_ACTIVE_COLOR = '#2BC2E1';

type Props = {
  title?: string;
  children: ReactNode;
  rightLinks?: ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  sidebarActive?: ProjectSidebarActive;
  projectId?: string;
};

const WebPageLayout: React.FC<Props> = ({
  title,
  children,
  rightLinks,
  hideHeader,
  hideFooter,
  sidebarActive,
  projectId: propProjectId,
}) => {
  const { t } = useTranslation('translation');
  const isMobileNav = useIsMobile(MOBILE_BREAKPOINT);
  const isMobileFooter = useIsMobile(FOOTER_BREAKPOINT);
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = useAppSelector(selectUserProfile);
  const [isMissingDocsModalOpen, setIsMissingDocsModalOpen] = useState(false);

  useEffect(() => {
    // 로그인, 회원가입 페이지에서는 안내 모달 띄우지 않음
    if (
      location.pathname === PATHS.LOGIN ||
      location.pathname === PATHS.LOGIN_EMAIL ||
      location.pathname === PATHS.SIGNUP ||
      location.pathname === PATHS.PASSWORD_FIND ||
      location.pathname === PATHS.DONATION_HISTORY_FIND
    ) {
      setIsMissingDocsModalOpen(false);
      return;
    }

    // 나의 계정 화면에서는 정산 서류 등록 안내 팝업을 띄우지 않음 (저장 직후 바로 뜨는 것 방지)
    // (삭제됨: 이제 저장 시 플래그를 세팅하므로 페이지 체크 불필요)

    // 회원가입 환영 화면에서 '나중에 하기'로 온 경우: 메인 프로젝트(/project)에서는 안내 모달을 띄우지 않음
    if (typeof window !== 'undefined' && localStorage.getItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY) === '1') {
      if (location.pathname === PATHS.PROJECT_MANAGEMENT || location.pathname === PATHS.DASHBOARD) {
        return;
      }
      localStorage.removeItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY);
    }

    // 회원가입 직후 나의 계정으로 이동한 경우: 모달 비표시. 그 외 경로로 이동 시 플래그 해제
    if (typeof window !== 'undefined' && localStorage.getItem(SUPPRESS_POST_SIGNUP_MODALS_KEY) === '1') {
      if (location.pathname === PATHS.MY_ACCOUNT || location.pathname.startsWith(`${PATHS.MY_ACCOUNT}/`)) {
        return;
      }
      localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
    }

    if (userProfile) {
      const isKorea = userProfile.bank_location_country_code === 'KR' || !userProfile.bank_location_country_code;
      const hasBankInfo = Boolean(userProfile.bank_account || userProfile.bank_name);
      const missingBankbook = !userProfile.bankbook_image_url;
      const missingId = !userProfile.registration_card_url;
      const isIndividual = isIndividualUserType(userProfile.user_type);
      const isOrganizationWithoutUniqueId = !isIndividual && !userProfile.business_registration;
      const needsId = isIndividual || !isOrganizationWithoutUniqueId;
      const shouldShowMissingDocs = isKorea && hasBankInfo && (missingBankbook || (needsId && missingId));

      if (shouldShowMissingDocs) {
        let shouldOpenDocs = true;
        if (typeof window !== 'undefined') {
          const hasShownDocs = localStorage.getItem(MISSING_DOCS_MODAL_SEEN_KEY) === '1';
          shouldOpenDocs = !hasShownDocs;
        }
        if (shouldOpenDocs) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(MISSING_DOCS_MODAL_SEEN_KEY, '1');
          }
          setIsMissingDocsModalOpen(true);
        } else if (isMissingDocsModalOpen) {
          // 이미 열려있다면 유지
        } else {
          setIsMissingDocsModalOpen(false);
        }
      } else {
        setIsMissingDocsModalOpen(false);
      }
    } else {
      // 로그아웃 상태 등 userProfile이 없으면 모달 닫기
      setIsMissingDocsModalOpen(false);
    }
  }, [userProfile, location.pathname, isMissingDocsModalOpen]);

  const handleCloseMissingDocsModal = () => {
    setIsMissingDocsModalOpen(false);
  };

  const currentProjectId = propProjectId || (location.state as { projectId?: string } | undefined)?.projectId;

  const showProjectSidebar = Boolean(sidebarActive) && !isMobileNav;

  const projectSidebarItems: Array<{
    key: ProjectSidebarActive;
    label: string;
    path: string;
    icon: string;
    activeIcon: string;
  }> = [
    {
      key: 'posts',
      label: t('common.mobileBottomNav.post'),
      path: PATHS.POST_MANAGEMENT,
      icon: postIcon,
      activeIcon: postActiveIcon,
    },
    {
      key: 'donors',
      label: t('common.mobileBottomNav.donors'),
      path: PATHS.DONOR_MANAGEMENT,
      icon: sponsorIcon,
      activeIcon: sponsorActiveIcon,
    },
    {
      key: 'payments',
      label: t('common.mobileBottomNav.payment'),
      path: PATHS.PAYMENT_STATUS,
      icon: payIcon,
      activeIcon: payActiveIcon,
    },
    {
      key: 'settings',
      label: t('common.mobileBottomNav.setting'),
      path: PATHS.PROJECT_SETTINGS,
      icon: projectIcon,
      activeIcon: projectActiveIcon,
    },
  ];

  return (
    <PageBackground>
      <Shell>
        {!hideHeader &&
          (isMobileNav ? (
            <MobileNavBar title={title} />
          ) : (
            <NavBar title={title} rightLinks={rightLinks} />
          ))}
        <Main $hasSidebar={showProjectSidebar}>
          <Content $hasSidebar={showProjectSidebar}>
            {showProjectSidebar ? (
              <ProjectShell>
                <ProjectSidebar>
                  <SidebarMenu>
                    {projectSidebarItems.map((it) => {
                      const active = sidebarActive === it.key;
                      return (
                        <SidebarItem
                          key={it.key}
                          type="button"
                          $active={active}
                          onClick={() => navigate(it.path, { state: { projectId: currentProjectId } })}
                        >
                          <ItemIconImg
                            src={active ? it.activeIcon : it.icon}
                            alt=""
                            aria-hidden="true"
                          />
                          <ItemText $active={active}>{it.label}</ItemText>
                        </SidebarItem>
                      );
                    })}
                  </SidebarMenu>
                </ProjectSidebar>
                <ProjectMain>{children}</ProjectMain>
              </ProjectShell>
            ) : (
              children
            )}
          </Content>
        </Main>
        {!hideFooter && (isMobileFooter ? <MobileFooter /> : <Footer />)}
      </Shell>
      <MissingDocumentsModal
        isOpen={isMissingDocsModalOpen}
        onClose={handleCloseMissingDocsModal}
        userProfile={userProfile}
      />
    </PageBackground>
  );
};

const PageBackground = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.grayScale[3]};
`;

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main<{ $hasSidebar?: boolean }>`
  flex: 1;
  display: flex;
  justify-content: ${({ $hasSidebar }) => ($hasSidebar ? 'flex-start' : 'center')};
  padding: 16px 0;
  padding-left: ${({ $hasSidebar }) => ($hasSidebar ? '47px' : '0')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${convertToVwMob(16)} ${convertToVwMob(0)};
    justify-content: stretch;
    padding-left: 0;
  }
`;

const Content = styled.div<{ $hasSidebar?: boolean }>`
  width: 100%;
  max-width: 1200px;
  min-height: 70vh;
  padding: ${({ $hasSidebar }) => ($hasSidebar ? '0 16px 0 0' : '0 16px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.colors.grayScale[80]};

  @media (max-width: ${FOOTER_BREAKPOINT}px) {
    padding: ${({ $hasSidebar }) => ($hasSidebar ? '0 12px 0 0' : '0 12px')};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: auto;
    padding: 0;
    gap: ${convertToVwMob(12)};
  }
`;

const SIDEBAR_WIDTH = 240;
const SIDEBAR_GAP = 88;
const NAVBAR_HEIGHT = 60;

const ProjectShell = styled.div`
  width: 100%;
  display: flex;
  gap: ${SIDEBAR_GAP}px;
  align-items: flex-start;
`;

const ProjectSidebar = styled.aside`
  position: sticky;
  top: ${NAVBAR_HEIGHT}px;
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.grayScale[3]};
  border-radius: 0;
  border: none;
  padding: 52px 12px 32px 0;
  box-sizing: border-box;
`;

const ProjectMain = styled.section`
  flex: 1;
  min-width: 0;
  border-radius: 16px;
  box-sizing: border-box;
`;

const SidebarMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;

`;

const SidebarItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  text-align: left;

  background: transparent;
  box-shadow: none;
  border-left: none;

  &:hover {
    background: ${({ theme }) => theme.colors.grayScale[2]};
  }
`;

const ItemIconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ItemText = styled.span<{ $active?: boolean }>`
  ${({ theme }) => theme.typography.contents.medium20}
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ theme, $active }) =>
    $active ? PROJECT_SIDEBAR_ACTIVE_COLOR : theme.colors.grayScale[90]};
`;

export default WebPageLayout;

